import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  getAllBusesConnectionMetrics,
  getConnectedBusesMetrics,
  getDisconnectedBusesMetrics,
  getAllBusesConnectionMetricsList,
  getConnectedBusesList,
  getDisconnectedBusesList,
} from "../../services/statisticsService";
import Paginator from "../../components/common/Paginator";
import { exportBusActivityExcel } from "../../utils/exportBusActivityExcel";
import BusActivityCardSkeleton from "./BusActivityCardSkeleton";
import SectionHeader from "../common/SectionHeader";
import ButtonEnhanced from "../common/ButtonEnhanced";
import ClearFiltersButton from "../common/ClearFiltersButton";
import BusFiltersCard from "./BusFiltersCard";

// Maps each tab to its paginated endpoint and to its list (unpaged) endpoint for export.
const ENDPOINTS = {
  all: { paged: getAllBusesConnectionMetrics, list: getAllBusesConnectionMetricsList },
  active: { paged: getConnectedBusesMetrics, list: getConnectedBusesList },
  inactive: { paged: getDisconnectedBusesMetrics, list: getDisconnectedBusesList },
};

export default function BusActivity() {
  const { token } = useAuth();

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);

  const [pageData, setPageData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Universe is fetched once (no filters) so the cascade dropdowns can list every
  // state/city/line/route present in the database, regardless of the current filter.
  const [universe, setUniverse] = useState([]);
  const [isLoadingUniverse, setIsLoadingUniverse] = useState(true);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const handleStateChange = (v) => {
    setSelectedState(v);
    setSelectedCity(null);
    setSelectedLine(null);
    setSelectedRoute(null);
    setPage(0);
  };
  const handleCityChange = (v) => {
    setSelectedCity(v);
    setSelectedLine(null);
    setSelectedRoute(null);
    setPage(0);
  };
  const handleLineChange = (v) => {
    setSelectedLine(v);
    setSelectedRoute(null);
    setPage(0);
  };
  const handleRouteChange = (v) => {
    setSelectedRoute(v);
    setPage(0);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedLine(null);
    setSelectedRoute(null);
    setPage(0);
  };

  const hasActiveFilters =
    startDate != null || endDate != null ||
    selectedState != null || selectedCity != null ||
    selectedLine != null || selectedRoute != null;

  // Universe: single unfiltered call to /all-connections-list. The result is reused
  // across tab changes so the cascade dropdown options are stable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) return;
      setIsLoadingUniverse(true);
      try {
        const list = await getAllBusesConnectionMetricsList(token);
        if (!cancelled) setUniverse(Array.isArray(list) ? list : []);
      } finally {
        if (!cancelled) setIsLoadingUniverse(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Server-side paginated fetch — runs on every filter change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) return;
      setIsLoading(true);
      try {
        const opts = { page, isAsc: false, sortBy: "busLastActivity" };
        if (startDate) opts.startDate = startDate;
        if (endDate) opts.endDate = endDate;
        if (selectedState != null) opts.staId = selectedState;
        if (selectedCity != null) opts.citId = selectedCity;
        if (selectedLine != null) opts.surId = selectedLine;
        if (selectedRoute != null) opts.rouId = selectedRoute;

        const fetcher = ENDPOINTS[filter].paged;
        const response = await fetcher(token, opts);

        if (cancelled) return;
        if (response && Array.isArray(response.content)) {
          setPageData(response.content);
          setTotalPages(response.totalPages || 1);
        } else {
          setPageData([]);
          setTotalPages(1);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error al cargar los autobuses:", error);
          setPageData([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, page, token, startDate, endDate, selectedState, selectedCity, selectedLine, selectedRoute]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    const cleaned = phone.replace(/\s+/g, "");
    const match = cleaned.match(/^(\+\d{2})(\d{3})(\d{7})$/);
    if (match) return `${match[1]} ${match[2]}-${match[3]}`;
    return phone;
  };

  const mapBusesData = (dataArray) => {
    return dataArray.map((item) => {
      const busInfo = item.bus || {};
      const routeInfo = busInfo.route || {};
      const superRouteInfo = busInfo.superRoute || {};
      const cityInfo = superRouteInfo.city || routeInfo.city || routeInfo.startStop?.city || {};

      const driversList = item.users || [];
      const ownerUser = busInfo.busOwner?.user;

      let driverName = "Sin Conductor Asignado";
      let driverIdCard = "N/A";
      let driverPhone = "N/A";

      if (driversList.length === 1) {
        const u = driversList[0];
        driverName = `${u.userName} ${u.userLastName || ""}`.trim();
        driverIdCard = u.userIdCard || "N/A";
        driverPhone = formatPhoneNumber(u.userPhone);
      } else if (driversList.length > 1) {
        driverName = `Múltiples Conductores (${driversList.length})`;
        driverIdCard = "Varios";
      } else if (ownerUser) {
        driverName = `${ownerUser.userName} ${ownerUser.userLastName || ""}`.trim() + " (Dueño)";
        driverIdCard = ownerUser.userIdCard || "N/A";
        driverPhone = formatPhoneNumber(ownerUser.userPhone);
      }

      let formattedLastConnection = "Nunca";
      if (busInfo.busLastActivity) {
        formattedLastConnection = new Date(busInfo.busLastActivity).toLocaleString("es-VE", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", hour12: true,
        });
      }

      const isActive = item.daysSinceLastConnection !== -1 && item.daysSinceLastConnection < 5;
      const stateInfo = superRouteInfo.state || cityInfo.state || routeInfo.state || {};

      return {
        id: busInfo.busId,
        name: busInfo.busName || `Unidad ${busInfo.busRegistrationId || busInfo.busId}`,
        plate: busInfo.busRegistrationId || "Sin Placa",
        brand: superRouteInfo.surName || "Sin Línea",
        route: routeInfo.rouName || "Sin Ruta",
        city: cityInfo.citName || "Sin Ciudad",
        surId: superRouteInfo.surId || null,
        rouId: routeInfo.rouId || null,
        citId: cityInfo.citId || null,
        staId: stateInfo.staId || null,
        staName: stateInfo.staName || null,
        driver: driverName,
        driverId: driverIdCard,
        phone: driverPhone,
        activeTours: busInfo.busTodayRuns || 0,
        daysConnected: busInfo.busTotalDays || 0,
        daysWithoutConnection: item.daysSinceLastConnection === -1 ? "-" : item.daysSinceLastConnection,
        lastConnection: formattedLastConnection,
        lastActivityRaw: busInfo.busLastActivity || null,
        isActive: isActive,
      };
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mappedBuses = useMemo(() => mapBusesData(pageData), [pageData]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mappedUniverse = useMemo(() => mapBusesData(universe), [universe]);

  const handleFilterChange = (newFilter) => {
    if (filter === newFilter) return;
    setFilter(newFilter);
    setPage(0);
  };

  // Excel export hits the matching list endpoint with the current filters,
  // so the file always reflects what the user is seeing.
  const handleExportExcel = async () => {
    if (!token) return;
    setIsExporting(true);
    try {
      const opts = {};
      if (startDate) opts.startDate = startDate;
      if (endDate) opts.endDate = endDate;
      if (selectedState != null) opts.staId = selectedState;
      if (selectedCity != null) opts.citId = selectedCity;
      if (selectedLine != null) opts.surId = selectedLine;
      if (selectedRoute != null) opts.rouId = selectedRoute;

      const listFetcher = ENDPOINTS[filter].list;
      const list = await listFetcher(token, opts);
      const data = Array.isArray(list) ? mapBusesData(list) : [];
      if (data.length === 0) {
        console.warn("No data to export with the current filters");
        return;
      }
      exportBusActivityExcel(data);
    } catch (error) {
      console.error("Error generando Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const isActionDisabled = isLoading || isExporting;

  return (
    <div style={{ flex: 1, backgroundColor: '#FFFFFF', overflowY: 'auto' }}>
      <div style={{ padding: '32px 24px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

        <SectionHeader
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="3" width="16" height="16" rx="2" ry="2"></rect>
              <path d="M4 11h16"></path><path d="M8 15h.01"></path><path d="M16 15h.01"></path><path d="M6 19v2"></path><path d="M18 19v2"></path>
            </svg>
          }
          title="Actividad de Unidades"
          subtitle="Monitorea el estado de conexión GPS y el rastreo operativo de todas las unidades del sistema."
        />

        <BusFiltersCard
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          selectedState={selectedState}
          selectedCity={selectedCity}
          selectedLine={selectedLine}
          selectedRoute={selectedRoute}
          onStateChange={handleStateChange}
          onCityChange={handleCityChange}
          onLineChange={handleLineChange}
          onRouteChange={handleRouteChange}
          buses={mappedUniverse}
          isLoadingUniverse={isLoadingUniverse}
          exportButton={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <ClearFiltersButton
                onClick={handleClearFilters}
                disabled={isActionDisabled || !hasActiveFilters}
              />
              <ButtonEnhanced
                variant="green"
                onClick={handleExportExcel}
                disabled={isActionDisabled}
                style={{ padding: '10px 20px', fontWeight: 600 }}
                icon={
                  isExporting ? (
                    <div style={{ width: 16, height: 16, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                  )
                }
              >
                {isExporting ? "Exportando Excel..." : "Exportar Excel"}
              </ButtonEnhanced>
            </div>
          }
        />
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>

        <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: 24, gap: 8 }}>
          {[
            { key: 'all', label: 'Todos' },
            { key: 'active', label: 'Buses Activos' },
            { key: 'inactive', label: 'Buses Inactivos' },
          ].map((tab) => {
            const isActive = filter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleFilterChange(tab.key)}
                disabled={isActionDisabled}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: 16,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#1F2937' : '#6B7280',
                  borderBottom: isActive ? '3px solid #EAC024' : '3px solid transparent',
                  cursor: isActionDisabled ? 'not-allowed' : 'pointer',
                  marginBottom: -1,
                  opacity: isActionDisabled ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {filter === "inactive" && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            backgroundColor: '#FEF3C7', borderLeft: '4px solid #F59E0B',
            padding: '12px 16px', borderRadius: '0 8px 8px 0',
            marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400E', lineHeight: '1.5' }}>
              <strong>Nota:</strong> Las unidades inactivas son aquellas que no han registrado conexión GPS ni actividad operativa durante <strong>5 días consecutivos o más</strong>.
            </p>
          </div>
        )}

        <hr style={{ borderTop: '1px solid rgb(229, 231, 235)', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', margin: '0px 0px 24px 0px' }} />

        {isLoading ? (
          <BusActivityCardSkeleton count={8} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {mappedBuses.map((bus) => (
                <div key={bus.id} style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>

                  <div style={{ backgroundColor: bus.isActive ? '#4F46E5' : '#9CA3AF', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ width: '32px', height: '32px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="3" width="16" height="16" rx="2" ry="2"></rect>
                        <path d="M4 11h16"></path><path d="M8 15h.01"></path><path d="M16 15h.01"></path><path d="M6 19v2"></path><path d="M18 19v2"></path>
                      </svg>
                    </div>
                    <div style={{ backgroundColor: '#ffc739', padding: '4px 10px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px' }} title="Recorridos Activos Hoy">
                      <svg width="14" height="14" fill="#B45309" viewBox="0 0 24 24">
                        <path d="M16,11.78L20.24,4.45L21.97,5.45L16.74,14.5L10.23,10.75L5.46,19H22V21H2V3H4V17.54L9.5,8L16,11.78Z"></path>
                      </svg>
                      <span style={{ color: '#B45309', fontWeight: 800, fontSize: '13px' }}>{bus.activeTours}</span>
                    </div>
                  </div>

                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 700, color: '#2E335B', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bus.name}</h3>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#3B82F6', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bus.plate}</p>

                    <div style={{ borderTop: '1px solid #E5E7EB', marginBottom: '12px' }}></div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#A855F7' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M5,3V21H11V17.5H13V21H19V3H5M7,5H9V7H7V5M11,5H13V7H11V5M15,5H17V7H15V5M7,9H9V11H7V9M11,9H13V11H11V9M15,9H17V11H15V9M7,13H9V15H7V13M11,13H13V15H11V13M15,13H17V15H15V13M7,17H9V19H7V17M15,17H17V19H15V17Z"></path></svg>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ fontWeight: 500 }}>Línea:</span> {bus.brand}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#14B8A6' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M6.5,8.11C5.61,8.11 4.89,7.39 4.89,6.5A1.61,1.61 0 0,1 6.5,4.89C7.39,4.89 8.11,5.61 8.11,6.5V6.5A1.61,1.61 0 0,1 6.5,8.11M6.5,2C4,2 2,4 2,6.5C2,9.87 6.5,14.86 6.5,14.86C6.5,14.86 11,9.87 11,6.5C11,4 9,2 6.5,2M17.5,16C16.23,16 15.1,16.8 14.68,18H9.32C8.77,16.44 7.05,15.62 5.5,16.17C3.93,16.72 3.11,18.44 3.66,20C4.22,21.56 5.93,22.38 7.5,21.83C8.35,21.53 9,20.85 9.32,20H14.69C15.24,21.56 16.96,22.38 18.5,21.83C20.08,21.28 20.9,19.56 20.35,18C19.92,16.8 18.78,16 17.5,16Z"></path></svg>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ fontWeight: 500 }}>Ruta:</span> {bus.route}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#3B82F6' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M19,15H17V13H19M19,19H17V17H19M13,7H11V5H13M13,11H11V9H13M13,15H11V13H13M13,19H11V17H13M7,11H5V9H7M7,15H5V13H7M7,19H5V17H7M15,11V5L12,2L9,5V7H3V21H21V11H15Z"></path></svg>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ fontWeight: 500 }}>Ciudad:</span> {bus.city}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#F59E0B' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"></path></svg>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ fontWeight: 500 }}>Conductor:</span> {bus.driver}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#A855F7' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M2,3H22C23.05,3 24,3.95 24,5V19C24,20.05 23.05,21 22,21H2C0.95,21 0,20.05 0,19V5C0,3.95 0.95,3 2,3M14,6V7H22V6H14M14,8V9H21.5L22,9V8H14M14,10V11H21V10H14M8,13.91C6,13.91 2,15 2,17V18H14V17C14,15 10,13.91 8,13.91M8,6A3,3 0 0,0 5,9A3,3 0 0,0 8,12A3,3 0 0,0 11,9A3,3 0 0,0 8,6Z"></path></svg>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ fontWeight: 500 }}>Cédula:</span> {bus.driverId}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B5563' }}>
                        <svg style={{ flexShrink: 0, color: '#22C55E' }} width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"></path></svg>
                        <span>{bus.phone}</span>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #E5E7EB', marginBottom: '12px' }}></div>

                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg style={{ color: '#10B981' }} width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"></path></svg>
                        Métricas de Conexión
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px', color: '#4B5563' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <svg style={{ flexShrink: 0, color: '#10B981', marginTop: '2px' }} width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M16,11.78L20.24,4.45L21.97,5.45L16.74,14.5L10.23,10.75L5.46,19H22V21H2V3H4V17.54L9.5,8L16,11.78Z"></path></svg>
                          <span style={{ wordBreak: 'break-word' }}><span style={{ fontWeight: 500 }}>Recorridos realizados ayer:</span> {bus.activeTours}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <svg style={{ flexShrink: 0, color: '#3B82F6', marginTop: '2px' }} width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"></path></svg>
                          <span style={{ wordBreak: 'break-word' }}><span style={{ fontWeight: 500 }}>Última vez conectado:</span> {bus.lastConnection}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <svg style={{ flexShrink: 0, color: '#818CF8', marginTop: '2px' }} width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"></path></svg>
                          <span><span style={{ fontWeight: 500 }}>Días conectados:</span> {bus.daysConnected}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <svg style={{ flexShrink: 0, color: '#FB923C', marginTop: '2px' }} width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M6,2H18V8H18V8L14,12L18,16V16H18V22H6V16H6V16L10,12L6,8V8H6V2M16,16.5L12,12.5L8,16.5V20H16V16.5M12,11.5L16,7.5V4H8V7.5L12,11.5M10,6H14V6.75L12,8.75L10,6.75V6Z"></path></svg>
                          <span><span style={{ fontWeight: 500 }}>Días sin conectarse:</span> {bus.daysWithoutConnection}</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </div>

            {mappedBuses.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                <p>No se encontraron unidades con los filtros aplicados.</p>
              </div>
            )}

            {totalPages > 0 && (
              <div className="mt-8 mb-4">
                <Paginator
                  currentPage={page}
                  totalPages={totalPages}
                  handlePageChange={(newPage) => setPage(newPage)}
                  itemsPerPage={10}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
